import React, { useState, useRef } from 'react';
import {
  HiCheckCircle,
  HiDotsHorizontal,
  HiPlus,
  HiOutlinePhotograph,
} from 'react-icons/hi';
import { useChecks } from '../context/ChecksContext';
import DatePicker from '../../../../components/DatePicker';

export default function CheckView() {
  const [activeTab, setActiveTab] = useState('Check Details');
  const [photos, setPhotos] = useState([]);

  const fileInputRef = useRef(null);

  const {
    slug,
    selectedDate,
    setSelectedDate,
    checklist,
    submission,
    loading,
  } = useChecks();

  const documentName = slug
    ? slug
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    : 'Checklist';

  const tabs = [
    'Check Details',
    'Checklist',
    `Photos (${photos.length})`,
    'History',
  ];

  const completedBy = submission?.completedBy?.fullName || '-';
  const submittedBy = submission?.submittedBy?.fullName || '-';

  const formattedSubmissionDate = submission?.submissionDate
    ? new Date(submission.submissionDate).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '-';

  const checkInformation = [
    {
      label: 'Checklist',
      value: checklist?.name || documentName,
    },
    {
      label: 'Submission Date',
      value: formattedSubmissionDate,
    },
    {
      label: 'Completed By',
      value: completedBy,
    },
    {
      label: 'Checked By',
      value: submittedBy,
    },
    {
      label: 'Opening Notes',
      value: submission?.openingNotes || '-',
    },
    {
      label: 'Closed / Secured',
      value: submission?.isClosedSecured ? 'Yes' : 'No',
    },
  ];

  const handlePhotoUpload = (event) => {
    const files = Array.from(event.target.files);

    if (files.length === 0) return;

    const newPhotoUrls = files.map((file) => URL.createObjectURL(file));

    setPhotos((prevPhotos) => [...prevPhotos, ...newPhotoUrls]);

    event.target.value = '';
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const isPassed =
    submission?.itemResults?.length > 0 &&
    submission.itemResults.every((result) => result.status === 'PASS');

  return (
    <div className="mx-auto w-full max-w-6xl rounded-lg border border-gray-100 bg-white p-6 font-sans text-gray-800 shadow-sm">
      <input
        type="file"
        ref={fileInputRef}
        onChange={handlePhotoUpload}
        accept="image/*"
        multiple
        className="hidden"
      />

      {/* Top Header */}
      <div className="flex flex-col gap-4 border-b border-gray-100 pb-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-emerald-200 bg-emerald-50 text-emerald-600">
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>

          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <h1 className="text-xl font-bold uppercase tracking-wide text-gray-900">
                {documentName} - View
              </h1>

              {submission && (
                <span className={`inline-flex items-center rounded border px-2 py-0.5 text-xs font-semibold ${
                  isPassed
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                    : 'border-rose-200 bg-rose-50 text-rose-700'
                }`}>
                  <HiCheckCircle className="mr-1 h-3.5 w-3.5" />
                  {isPassed ? 'Pass' : 'Issue'}
                </span>
              )}
            </div>

            <p className="mt-0.5 text-xs text-gray-500">
              Record ID:{' '}
              <span className="font-mono">
                {submission?.id ? `CP-${submission.id}` : '-'}
              </span>
            </p>
          </div>
        </div>

        {/* Right Side */}
        <div className="flex flex-wrap items-center justify-between gap-3 sm:justify-end">
          <DatePicker
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
          />

          <button
            type="button"
            className="rounded-full p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <HiDotsHorizontal className="h-6 w-6" />
          </button>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="py-10 text-center text-sm text-gray-400">
          Loading check...
        </div>
      )}

      {/* No submission */}
      {!loading && !submission && (
        <div className="py-10 text-center">
          <p className="text-sm font-medium text-gray-500">
            No submission found for {selectedDate}.
          </p>
        </div>
      )}

      {/* Existing Submission */}
      {!loading && submission && (
        <>
          {/* Tabs */}
          <div className="mt-2 flex gap-4 overflow-x-auto border-b border-gray-200 sm:gap-6">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`shrink-0 border-b-2 px-1 py-3 text-sm font-medium transition-all duration-150 ${
                  activeTab.startsWith(tab.split(' ')[0])
                    ? 'border-blue-600 font-semibold text-blue-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 gap-8 pt-6 lg:grid-cols-12">
            {/* Check Information */}
            <div className="lg:col-span-5">
              <h2 className="mb-4 text-sm font-bold uppercase tracking-wider text-gray-900">
                Check Information
              </h2>

              <div className="space-y-3">
                {checkInformation.map((info) => (
                  <div key={info.label} className="grid grid-cols-3 gap-2 text-sm">
                    <span className="font-medium text-gray-500">
                      {info.label}
                    </span>

                    <span className="col-span-2 break-words font-medium text-gray-900">
                      {info.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Photos */}
            <div className="lg:col-span-7">
              <h2 className="mb-4 text-sm font-bold uppercase tracking-wider text-gray-900">
                Photos
              </h2>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {submission.photoUrl && (
                  <div className="group relative aspect-[4/3] overflow-hidden rounded-xl border border-gray-100 shadow-sm">
                    <img
                      src={submission.photoUrl}
                      alt="Checklist submission"
                      className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
                    />
                  </div>
                )}

                {photos.map((photoUrl, index) => (
                  <div
                    key={index}
                    className="group relative aspect-[4/3] overflow-hidden rounded-xl border border-gray-100 shadow-sm"
                  >
                    <img
                      src={photoUrl}
                      alt={`Uploaded inspection photo ${index + 1}`}
                      className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
                    />
                  </div>
                ))}

                {!submission.photoUrl && photos.length === 0 && (
                  <div className="flex aspect-auto flex-col items-center justify-center rounded-xl border border-dashed border-gray-200 bg-gray-50/30 px-4 py-6 text-center sm:col-span-2 sm:aspect-initial">
                    <HiOutlinePhotograph className="mb-1 h-8 w-8 text-gray-300" />
                    <p className="text-sm font-medium text-gray-400">
                      No photos found
                    </p>
                  </div>
                )}

                <button
                  type="button"
                  onClick={triggerFileInput}
                  className="group flex aspect-[4/3] flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 bg-gray-50/50 text-gray-500 transition-all hover:border-blue-400 hover:bg-blue-50/20 hover:text-blue-600"
                >
                  <HiPlus className="mb-1 h-6 w-6 text-gray-400 transition-colors group-hover:text-blue-500" />
                  <span className="text-xs font-semibold">
                    Add Photo
                  </span>
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}